/**
 * @file Hook de gestion des étapes et du formulaire du widget de réservation.
 * @module packages.common.components.table-reservation.hooks.useReservationSteps
 *
 * Définit les étapes du stepper (fixes et conditionnelles),
 * gère l'état du formulaire, la navigation entre étapes
 * et la validation. Les étapes FORMULA et PAYMENT sont
 * injectées dynamiquement selon le contexte de réservation.
 */
import {useState, useCallback, useEffect, useMemo} from 'react';
import uuidv4 from 'packages/common/utils/uuidv4';
import {TIMING} from '../constants/timing';
import {BASE_STEPS} from '../constants/steps';
import {maxStep, STEP_ORDER} from '../utils/stepSummary';

// Réexport de commodité : plusieurs modules importent `BASE_STEPS` depuis ce
// hook. La source de vérité reste `../constants/steps`, fichier autonome qui
// évite le cycle d'imports avec `stepSummary` (utilitaire bas niveau).
export {BASE_STEPS};

/**
 * Calcule la liste ordonnée des étapes du stepper selon le contexte.
 *
 * Le stepper démarre à DATE (SERVICE_GUESTS et CONFIRMATION sont des écrans
 * hors stepper). FORMULA, DETAILS et PAYMENT sont insérées conditionnellement.
 *
 * @param {boolean} paymentRequired - Si l'empreinte bancaire est requise
 * @param {boolean} formulasRequired - Si l'étape formules est requise
 * @param {boolean} hasOptionalFields - Au moins un champ de l'étape Détails est à demander (champ standard activé ou question personnalisée)
 * @param {boolean} roomSelectionEnabled - Si l'étape de choix de salle est requise (≥ 2 salles réservables)
 * @return {{steps: string[], mapping: Object, totalSteps: number, lastStep: string}}
 *     `steps` (étapes logiques ordonnées), `mapping` (étape logique -> indice
 *     réel du Stepper), `totalSteps`, `lastStep` (dernière étape du parcours).
 */
export function calculateAvailableSteps(paymentRequired = false, formulasRequired = false, hasOptionalFields = true, roomSelectionEnabled = false) {
  // SERVICE_GUESTS est hors stepper (écran d'accueil plein écran)
  const steps = [BASE_STEPS.DATE];

  // ROOM s'insère entre DATE et TIME : la salle doit être connue avant de calculer
  // les créneaux pour qu'ils s'adaptent à la salle choisie. Conditionnée à la
  // présence d'au moins deux salles réservables (cf. useRoomSelection).
  if (roomSelectionEnabled) {
    steps.push(BASE_STEPS.ROOM);
  }

  steps.push(BASE_STEPS.TIME);

  // FORMULA s'insère entre TIME et CONTACT si des formules sont disponibles
  if (formulasRequired) {
    steps.push(BASE_STEPS.FORMULA);
  }

  steps.push(BASE_STEPS.CONTACT);

  // DETAILS n'apparaît que s'il reste quelque chose à demander : un champ
  // standard activé (settings.optionalFields) ou une question personnalisée.
  // Sinon on évite d'afficher une étape vide.
  if (hasOptionalFields) {
    steps.push(BASE_STEPS.DETAILS);
  }

  // PAYMENT s'ajoute en fin de parcours si une empreinte est requise
  if (paymentRequired) {
    steps.push(BASE_STEPS.PAYMENT);
  }

  // CONFIRMATION est hors stepper (page séparée post-soumission)

  // Mapping étape logique -> indice réel pour le composant Stepper
  const stepMapping = {};
  steps.forEach((logicalStep, realIndex) => {
    stepMapping[logicalStep] = realIndex;
  });

  return {
    steps,
    mapping: stepMapping,
    totalSteps: steps.length,
    lastStep: steps[steps.length - 1],
  };
}

/**
 * Gère l'état complet du parcours de réservation : étapes, formulaire, navigation et validation.
 *
 * Recalcule dynamiquement les étapes disponibles selon les flags paymentRequired
 * et formulasRequired du formulaire. Fournit des primitives de navigation
 * (suivant, précédent, aller à) avec validation et transition animée.
 *
 * @return {Object} État et actions du parcours — contient `currentStep`
 *     (étape logique BASE_STEPS), `maxStepReached` (étape la plus avancée
 *     jamais atteinte, pour l'historique du breadcrumb), `formData`,
 *     `validationErrors`, `availableSteps` (`{steps, mapping, totalSteps,
 *     lastStep}`), `isLastStep`, les primitives `updateFormData`,
 *     `goToNextStep`, `goToPreviousStep`, `goToStep`, `resetSteps`,
 *     `validateCurrentStep`, `setValidationFunction`, `setErrors`, et l'alias
 *     `STEPS` (BASE_STEPS).
 */
export function useReservationSteps() {
  const [currentStep, setCurrentStep] = useState(BASE_STEPS.SERVICE_GUESTS);
  // Étape la plus avancée jamais atteinte. Sert au breadcrumb pour conserver
  // l'historique grisé même si l'utilisateur revient en arrière (clic chip).
  // Ne régresse jamais ; le composant breadcrumb filtre ensuite par
  // intersection avec `availableSteps.steps` pour gérer la disparition
  // d'étapes conditionnelles (formule/paiement) suite à un changement amont.
  const [maxStepReached, setMaxStepReached] = useState(BASE_STEPS.SERVICE_GUESTS);
  const [stepValidation, setStepValidation] = useState(() => () => true);
  const [validationErrors, setValidationErrors] = useState({});

  // Initialisation paresseuse : le draftId (identifiant du brouillon de parcours,
  // cf. useReservationDraftSync) ne doit être généré qu'une fois par parcours,
  // pas à chaque rendu.
  const [formData, setFormData] = useState(() => ({
    draftId: uuidv4(),
    service: '',
    partySize: 0,
    date: null,
    // Choix de salle (si activé côté restaurant) : salle demandée par le client et
    // liste des salles réservables. `roomSelectionEnabled` pilote l'insertion de
    // l'étape ROOM (renseigné par useRoomSelection).
    selectedAreaId: null,
    availableRooms: [],
    roomSelectionEnabled: false,
    timeSlot: null,
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    // Case newsletter cochée par défaut (décision produit) ; décochée, elle ne désinscrit jamais
    subscribeNewsletter: true,
    specialRequests: '',
    occasion: '',
    hasAllergies: false,
    allergyDetails: '',
    hasGiftVoucher: false,
    giftVoucherCode: '',
    // Activation par champ de la section « Détails supplémentaires ».
    // Alimenté depuis `config.optionalFields` au montage de TableReservationView.
    // `null` = config pas encore reçue → on affiche tout par défaut.
    optionalFields: null,
    // Questions personnalisées posées par l'établissement (`config.customQuestions`)
    // et réponses du client par identifiant de question.
    customQuestions: [],
    customAnswers: {},
    paymentRequired: false,
    paymentMethodId: null,
    paymentIntentId: null,
    cardHoldAmount: 0,
    paymentConfig: null,
    // Confirmation Stripe acquise pour l'intent courant (cf. utils/confirmCardHold)
    confirmedIntent: null,
    // Créneau perdu après le choix (service complet, blocage) : `{code, cardValidated}`
    slotLost: null,
    // Signal empreinte embarqué dans le brouillon (cf. usePaymentSignalTracker)
    paymentSignal: null,
    selectedFormulas: [],
    availableFormulas: [],
    unavailableFormulas: [],
    formulasRequired: false,
    termsAccepted: false,
  }));
  const availableSteps = useMemo(() => {
    // `optionalFields` non encore reçu → on garde l'étape par défaut.
    // Sinon on la masque uniquement si TOUS les champs sont désactivés.
    const opt = formData.optionalFields;
    const hasOptionalFields = !opt ||
      opt.allergies !== false ||
      opt.giftVoucher !== false ||
      opt.occasion !== false ||
      opt.specialRequests !== false ||
      formData.customQuestions?.length > 0;
    // Une inscription en liste d'attente ne réserve aucune table : le restaurateur
    // place le client si une place se libère. Choisir une salle n'y a pas de sens,
    // et toutes les salles y seraient affichées complètes.
    const roomStepEnabled = formData.roomSelectionEnabled && !formData.isWaitlistDate;
    return calculateAvailableSteps(formData.paymentRequired, formData.formulasRequired, hasOptionalFields, roomStepEnabled);
  }, [formData.paymentRequired, formData.formulasRequired, formData.optionalFields, formData.customQuestions, formData.roomSelectionEnabled, formData.isWaitlistDate]);

  // Filet de sécurité : si `availableSteps` est recomputé et que `currentStep`
  // n'en fait plus partie (cas rare où `paymentRequired` ou `formulasRequired`
  // passe à `false` après que l'utilisateur a déjà atteint l'étape
  // correspondante — ex. modif amont via breadcrumb), on rebascule vers
  // l'étape valide la plus proche en arrière. Sans ça, le renderer rendrait
  // `null` et l'utilisateur verrait un écran blanc jusqu'à navigation manuelle.
  // SERVICE_GUESTS et CONFIRMATION sont hors stepper donc toujours valides.
  useEffect(() => {
    if (currentStep === BASE_STEPS.SERVICE_GUESTS) return;
    if (currentStep === BASE_STEPS.CONFIRMATION) return;
    if (availableSteps.mapping[currentStep] !== undefined) return;

    const currentIndex = STEP_ORDER.indexOf(currentStep);
    for (let i = currentIndex - 1; i >= 0; i--) {
      const candidate = STEP_ORDER[i];
      if (availableSteps.mapping[candidate] !== undefined ||
          candidate === BASE_STEPS.SERVICE_GUESTS) {
        setCurrentStep(candidate);
        return;
      }
    }
    setCurrentStep(BASE_STEPS.SERVICE_GUESTS);
  }, [currentStep, availableSteps]);

  // Le bouton "Suivant" devient "Confirmer" sur la dernière étape
  const isLastStep = useMemo(() => {
    return currentStep === availableSteps.lastStep;
  }, [currentStep, availableSteps.lastStep]);

  // Résout l'étape suivante en tenant compte des étapes conditionnelles
  const getNextLogicalStep = useCallback(() => {
    // SERVICE_GUESTS est hors stepper, la suite est toujours DATE
    if (currentStep === BASE_STEPS.SERVICE_GUESTS) {
      return BASE_STEPS.DATE;
    }

    const currentRealIndex = availableSteps.mapping[currentStep];
    if (currentRealIndex === undefined || currentRealIndex >= availableSteps.steps.length - 1) {
      return null;
    }
    return availableSteps.steps[currentRealIndex + 1];
  }, [currentStep, availableSteps]);

  // Résout l'étape précédente, symétrique de getNextLogicalStep
  const getPreviousLogicalStep = useCallback(() => {
    // Retour vers l'écran d'accueil (hors stepper)
    if (currentStep === BASE_STEPS.DATE) {
      return BASE_STEPS.SERVICE_GUESTS;
    }

    const currentRealIndex = availableSteps.mapping[currentStep];
    if (currentRealIndex === undefined || currentRealIndex <= 0) {
      return null;
    }
    return availableSteps.steps[currentRealIndex - 1];
  }, [currentStep, availableSteps]);

  // Efface aussi l'erreur du champ modifié pour un feedback instantané
  const updateFormData = useCallback((field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Supprime l'erreur liée à ce champ pour un feedback UX instantané
    setValidationErrors((prev) => {
      const newErrors = {...prev};
      delete newErrors[field];
      return newErrors;
    });
  }, []);

  const goToNextStep = useCallback(() => {
    if (stepValidation()) {
      const nextStep = getNextLogicalStep();
      if (nextStep !== null) {
        // Petit délai pour éviter un flash visuel lors de la transition
        setTimeout(() => {
          setCurrentStep(nextStep);
          setMaxStepReached((prev) => maxStep(prev, nextStep));
          setValidationErrors({});
        }, TIMING.STEP_TRANSITION);
        return true;
      }
    }
    return false;
  }, [stepValidation, getNextLogicalStep]);

  // Validation de l'étape affichée sans navigation : la dernière étape envoie la
  // réservation au lieu d'avancer, elle doit pourtant passer par la même garde.
  const validateCurrentStep = useCallback(() => Boolean(stepValidation()), [stepValidation]);

  const goToPreviousStep = useCallback(() => {
    const previousStep = getPreviousLogicalStep();
    if (previousStep !== null) {
      setTimeout(() => {
        setCurrentStep(previousStep);
        setValidationErrors({});
      }, TIMING.STEP_TRANSITION);
    }
  }, [getPreviousLogicalStep]);

  // CONFIRMATION et SERVICE_GUESTS sont toujours accessibles même si hors stepper
  const goToStep = useCallback((step) => {
    if (step === BASE_STEPS.CONFIRMATION ||
        step === BASE_STEPS.SERVICE_GUESTS ||
        availableSteps.mapping[step] !== undefined) {
      // Délai minime pour fluidifier la transition CSS
      setTimeout(() => {
        setCurrentStep(step);
        setMaxStepReached((prev) => maxStep(prev, step));
        setValidationErrors({});
      }, TIMING.STEP_GOTO_TRANSITION);
    }
  }, [availableSteps.mapping]);

  // Remet le formulaire à zéro et retourne à l'écran d'accueil
  // Retour en arrière qui invalide les étapes suivantes : après un créneau perdu,
  // le client repart du calendrier et les pastilles Heure/Contact/Paiement ne
  // doivent plus être proposées tant qu'il n'a pas rechoisi. `goToStep` ne fait
  // jamais régresser `maxStepReached`, d'où cette variante dédiée.
  const rewindToStep = useCallback((step) => {
    setTimeout(() => {
      setCurrentStep(step);
      setMaxStepReached(step);
      setValidationErrors({});
    }, TIMING.STEP_GOTO_TRANSITION);
  }, []);

  const resetSteps = useCallback(() => {
    setCurrentStep(BASE_STEPS.SERVICE_GUESTS);
    setMaxStepReached(BASE_STEPS.SERVICE_GUESTS);
    setFormData((prev) => ({
      // Nouveau parcours = nouveau brouillon : l'ancien draftId ne doit jamais
      // être réutilisé (il référence un parcours déjà finalisé ou abandonné)
      draftId: uuidv4(),
      service: '',
      partySize: 0,
      date: null,
      // Choix de salle remis à zéro, mais la config des salles (liste + activation)
      // est préservée : elle vient de la config restaurant et useRoomSelection ne
      // se relance pas sur un reset, donc on ne la perd pas entre deux parcours.
      selectedAreaId: null,
      availableRooms: prev.availableRooms || [],
      roomSelectionEnabled: prev.roomSelectionEnabled || false,
      // Même raison pour les champs de l'étape Détails, issus de la config
      // restaurant : seules les réponses du client repartent de zéro.
      optionalFields: prev.optionalFields ?? null,
      customQuestions: prev.customQuestions || [],
      customAnswers: {},
      timeSlot: null,
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      subscribeNewsletter: true,
      specialRequests: '',
      occasion: '',
      hasAllergies: false,
      allergyDetails: '',
      hasGiftVoucher: false,
      giftVoucherCode: '',
      paymentRequired: false,
      paymentMethodId: null,
      paymentIntentId: null,
      cardHoldAmount: 0,
      cardHoldDescription: '',
      paymentConfig: null,
      paymentSignal: null,
      selectedFormulas: [],
      availableFormulas: [],
      unavailableFormulas: [],
      formulasRequired: false,
      termsAccepted: false,
    }));
    setValidationErrors({});
  }, []);

  // Chaque composant d'étape enregistre sa propre logique de validation
  const setValidationFunction = useCallback((validationFn) => {
    setStepValidation(() => validationFn);
  }, []);

  // Permet aux handlers externes de signaler des erreurs (ex: erreur API)
  const setErrors = useCallback((errors) => {
    setValidationErrors(errors);
  }, []);

  return {
    currentStep,
    maxStepReached,
    formData,
    validationErrors,
    availableSteps,
    isLastStep,
    updateFormData,
    goToNextStep,
    goToPreviousStep,
    goToStep,
    rewindToStep,
    resetSteps,
    validateCurrentStep,
    setValidationFunction,
    setErrors,
    // Rétro-compatibilité avec les composants utilisant STEPS
    STEPS: BASE_STEPS,
  };
}
